import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useState } from 'react';
import { DataGrid, GridColumn, GridItem, TextField, Typography } from '@emburse/embark-core';
import { debounce } from 'lodash';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
function ExampleContent({ selectedField }) {
    useTranslation();
    const { ExampleData } = selectedField;
    const exampleColumns = [
        {
            field: 'column',
            headerName: $t('Line Number'),
            sortable: false,
            flex: 1
        },
        {
            field: 'example',
            headerName: $t('Example'),
            sortable: false,
            flex: 3
        }
    ];
    const exampleRows = ExampleData.map((row, index) => ({
        id: index,
        column: index + 1,
        example: row,
        noData: ''
    }));
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { children: $t('See more values from the selected column below:') }), _jsx(DataGrid, { autoHeight: true, rows: exampleRows, hideFooter: true, columns: exampleColumns })] }));
}
const Users = forwardRef(({ selectedField, saveHandler }, ref) => {
    useTranslation();
    const { AdditionalAttribute } = selectedField;
    const [formData, setFormData] = useState(AdditionalAttribute);
    const [errorField, setErrorField] = useState('');
    useImperativeHandle(ref, () => ({
        onSave() {
            saveHandler(formData);
        }
    }));
    const hasDuplicates = (arr) => arr.length !== new Set(arr).size;
    const currentFieldHasDuplicates = (val) => {
        const currentProperty = JSON.parse(JSON.stringify(val));
        if (currentProperty) {
            const currentPropertyArray = currentProperty.split(',').map((value) => value.trim());
            return hasDuplicates(currentPropertyArray);
        }
        return false;
    };
    const hasMatchingValues = (prop, newVal) => {
        const formDataCopy = { ...formData };
        if (currentFieldHasDuplicates(newVal)) {
            return true;
        }
        delete formDataCopy[prop];
        const propertyArray = Object.values(formDataCopy)
            .map((val) => val.split(','))
            .flat()
            .map((value) => value.trim());
        const newValArray = newVal.split(',').map((value) => value.trim());
        const mergedArray = [...propertyArray, ...newValArray];
        return hasDuplicates(mergedArray);
    };
    const updateFormData = debounce((val, prop) => {
        setErrorField('');
        if (hasMatchingValues(prop, val)) {
            setErrorField(prop);
        }
        setFormData((prevState) => ({ ...prevState, ...{ [prop]: val } }));
    }, 500);
    const hasError = (field) => errorField === field;
    return (_jsxs(GridColumn, { spacing: 2, alignItems: "stretch", children: [_jsxs(GridItem, { children: [_jsx(Typography, { children: $t(`You selected the 'User Sync' Interchange Type, with 'Employee Role' as the Certify field to map to this column.
                    Please indicate the role(s) to be mapped to the following user roles:`) }), _jsx(Typography, { variant: "body2", style: { marginTop: '10px' }, children: $t('Note: These are case sensitive and should match what is in your setup') })] }), _jsx(GridItem, { children: _jsx(TextField, { label: $t('Employee'), fullWidth: true, onChange: (e) => updateFormData(e.target.value, 'Employee'), defaultValue: formData?.Employee, helperText: hasError('Employee')
                        ? $t('Cannot be duplicated')
                        : $t('Example: Assistant, Project Coordinator, etc...'), error: hasError('Employee') }) }), _jsx(GridItem, { children: _jsx(TextField, { label: $t('Manager'), fullWidth: true, onChange: (e) => updateFormData(e.target.value, 'Manager'), defaultValue: formData?.Manager, helperText: hasError('Manager')
                        ? $t('Cannot be duplicated')
                        : $t('Example: Director, Project Manager, etc...'), error: hasError('Manager') }) }), _jsx(GridItem, { children: _jsx(TextField, { label: $t('Executive'), fullWidth: true, onChange: (e) => updateFormData(e.target.value, 'Executive'), defaultValue: formData?.Executive, helperText: hasError('Executive')
                        ? $t('Cannot be duplicated')
                        : $t('Example: Vice President, CEO, etc...'), error: hasError('Executive') }) }), _jsx(GridItem, { children: _jsx(TextField, { label: $t('Accountant'), fullWidth: true, onChange: (e) => updateFormData(e.target.value, 'Accountant'), defaultValue: formData?.Accountant, helperText: hasError('Accountant')
                        ? $t('Cannot be duplicated')
                        : $t('Example: Analyst, CPA, etc...'), error: hasError('Accountant') }) })] }));
});
export { ExampleContent, Users };
